/* Fireflies.ai vault — API-key + meeting-sync. */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, RefreshCw, Loader2, Eye, EyeOff, Mic, ExternalLink } from "lucide-react";
import { SubSection } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  Fireflies vault — api_key + test + recent meetings preview       */
/* ----------------------------------------------------------------- */
function FirefliesVault({ cfg, onSaved }) {
    const { t, i18n } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [meBusy, setMeBusy] = useState(false);
    const [me, setMe] = useState(null);
    const [recentBusy, setRecentBusy] = useState(false);
    const [recent, setRecent] = useState(null);

    const save = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/fireflies", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "Fireflies" }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: "Fireflies" }))) return;
        setBusy(true);
        try { await api.delete("/integrations/fireflies"); toast.success(t("integr.deleted")); setMe(null); setRecent(null); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };
    const testKey = async () => {
        setMeBusy(true);
        try {
            const r = await api.get("/integrations/fireflies/me");
            setMe(r.data.user || {});
            toast.success(t("integr.fireflies_valid"));
        } catch (e) { setMe(null); toast.error(e?.response?.data?.detail || t("integr.test_failed")); }
        finally { setMeBusy(false); }
    };
    const loadRecent = async () => {
        setRecentBusy(true);
        try {
            const r = await api.get("/integrations/fireflies/recent?limit=5");
            setRecent(r.data.transcripts || []);
            toast.success(t("integr.meetings_loaded", { n: r.data.count }));
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.meetings_failed")); }
        finally { setRecentBusy(false); }
    };

    return (
        <div className="space-y-4">
            <SubSection title={`1. ${t("integr.sec_api_key")}`} testid="fireflies-credentials">
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Fireflies API Key</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || t("integr.paste_key")}
                            type={reveal ? "text" : "password"}
                            data-testid="fireflies-apikey-input"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy} data-testid="fireflies-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {t("integr.fireflies_where")} <span className="text-[var(--jr-text-2)]">app.fireflies.ai → Integrations → Fireflies API</span>.
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_current_key")}
                    </button>
                )}
            </SubSection>

            <SubSection title={`2. ${t("integr.sec_test_conn")}`}
                action={
                    <button onClick={testKey} disabled={meBusy || !cfg.configured} data-testid="fireflies-test"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 disabled:opacity-50">
                        {meBusy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} {t("integr.test_key")}
                    </button>
                }
                testid="fireflies-test-section">
                {!me ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.fireflies_test_hint")}</p>
                ) : (
                    <div className="rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] p-3 text-xs text-[var(--jr-text-2)] inline-flex items-start gap-2"
                        data-testid="fireflies-me-result">
                        <CheckCircle2 size={14} className="text-[#22c55e] mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white">{me.name || me.email || me.user_id}</div>
                            <div className="text-[var(--jr-text-3)]">{me.num_transcripts ?? 0} {t("integr.transcripts_available")} · {me.email}</div>
                        </div>
                    </div>
                )}
            </SubSection>

            <SubSection title={`3. ${t("integr.sec_recent_meetings")}`}
                action={
                    <button onClick={loadRecent} disabled={recentBusy || !cfg.configured} data-testid="fireflies-recent"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                        {recentBusy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} {t("common.refresh")}
                    </button>
                }
                testid="fireflies-recent-section">
                {recent === null ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.fireflies_recent_hint")}</p>
                ) : recent.length === 0 ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.no_transcripts")}</p>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {recent.map((m) => (
                            <li key={m.id} className="py-2.5 flex items-start gap-3" data-testid={`fireflies-recent-${m.id}`}>
                                <Mic size={14} className="text-[var(--jr-info)] mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm text-white truncate">{m.title || t("integr.untitled_meeting")}</span>
                                        {m.date && (
                                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                                                {new Date(m.date).toLocaleDateString(i18n.language === "en" ? "en-GB" : i18n.language === "fr" ? "fr-BE" : "nl-BE")}
                                            </span>
                                        )}
                                    </div>
                                    {m.summary?.short_summary && (
                                        <p className="mt-1 text-[11px] text-[var(--jr-text-2)] line-clamp-2">{m.summary.short_summary}</p>
                                    )}
                                    {m.summary?.action_items?.length > 0 && (
                                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">
                                            <strong className="text-[var(--jr-text-2)]">{m.summary.action_items.length}</strong> {t("integr.action_items")}
                                        </p>
                                    )}
                                </div>
                                {m.transcript_url && (
                                    <a href={m.transcript_url} target="_blank" rel="noreferrer"
                                        className="p-1.5 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                        <ExternalLink size={11} />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SubSection>
        </div>
    );
}

export default FirefliesVault;
